import React, { useState, useEffect } from 'react';
import { useAuth, auth } from './auth-context';
import { signOut } from 'firebase/auth';
import { Link } from 'react-router-dom';
import {
    Menu,
    X,
    ArrowLeft,
    ChartBar,
    Brain,
    Target,
    LogOut,
    RotateCw,
    ThumbsUp,
    ThumbsDown,
    Crown
} from 'lucide-react';
import { FlashcardQuestions } from './Questions/flashcard-questions';
import { fetchUserStats, updateAbilityEstimate, logResponse } from './firebase-utils';
import {
    processResponse as eapProcessResponse,
    difficultyToLogit,
    fisherInformation,
    classifyResponse,
    createDefaultAbilityEstimate,
    migrateFromDeltaRule
} from './lib/eap-estimator';

const Flashcards = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const { user, isPaidUser } = useAuth();
    const [isSidebarOpen, setSidebarOpen] = useState(false);
    const [currentCard, setCurrentCard] = useState(null);
    const [isFlipped, setIsFlipped] = useState(false);
    const [cardStartTime, setCardStartTime] = useState(null);
    const [abilityEstimate, setAbilityEstimate] = useState(createDefaultAbilityEstimate());
    const [lastPracticedByDomain, setLastPracticedByDomain] = useState({});
    const [cardHistory, setCardHistory] = useState(new Set());
    const [domainMastery, setDomainMastery] = useState({
        'Security Operations': 0,
        'General Security Concepts': 0,
        'Threats, Vulnerabilities and Mitigations': 0,
        'Security Program Management': 0,
        'Security Architecture': 0
    });
    const [sessionStats, setSessionStats] = useState({
        reviewed: 0,
        correct: 0,
        streak: 0
    });

    const selectNextCard = (history = cardHistory) => {
        const availableCards = FlashcardQuestions.filter(card => !history.has(card.id));

        if (availableCards.length === 0) {
            setCardHistory(new Set());
            return FlashcardQuestions[Math.floor(Math.random() * FlashcardQuestions.length)];
        }

        // Fisher information-based selection using domain-specific theta
        const scored = availableCards.map(card => {
            const theta = abilityEstimate?.byDomain?.[card.domain]?.mean ?? abilityEstimate?.global?.mean ?? 0;
            const diffLogit = difficultyToLogit(card.difficulty);
            const info = fisherInformation(theta, diffLogit);
            return { card, info };
        });

        scored.sort((a, b) => b.info - a.info);
        return scored[0].card;
    };

    const handleCardFlip = () => {
        setIsFlipped(!isFlipped);
    };

    const handleResponse = async (correct) => {
        if (!currentCard) return;

        // Capture response time (from when card was first shown)
        const responseTimeMs = cardStartTime ? performance.now() - cardStartTime : null;

        // EAP ability update
        const diffLogit = difficultyToLogit(currentCard.difficulty);
        const updatedEstimate = eapProcessResponse(
            abilityEstimate,
            currentCard.domain,
            diffLogit,
            correct
        );
        setAbilityEstimate(updatedEstimate);

        if (user) {
            updateAbilityEstimate(user.uid, updatedEstimate);
        }

        // Classify response
        const quadrant = responseTimeMs
            ? classifyResponse(correct, responseTimeMs, currentCard.difficulty)
            : (correct ? 'fluent' : 'gap');

        // Log response
        if (user) {
            logResponse(user.uid, {
                itemId: currentCard.id,
                domain: currentCard.domain,
                correct,
                responseTimeMs: responseTimeMs ? Math.round(responseTimeMs) : null,
                quadrant,
                mode: 'flashcard'
            });
        }

        // Note: Flashcards do NOT update domain mastery — only practice tests do.
        // Ability estimate and response logging above are still tracked for card selection.

        // Update session stats
        setSessionStats(prev => ({
            reviewed: prev.reviewed + 1,
            correct: prev.correct + (correct ? 1 : 0),
            streak: correct ? prev.streak + 1 : 0
        }));

        // Update card history
        const newHistory = new Set([...cardHistory, currentCard.id]);
        setCardHistory(newHistory);

        // Get next card
        const nextCard = selectNextCard(newHistory);
        setCurrentCard(nextCard);
        setCardStartTime(performance.now());
        setIsFlipped(false);
    };

    useEffect(() => {
        const loadUserData = async () => {
            if (!user) {
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                const stats = await fetchUserStats(user.uid);
                setDomainMastery(stats.domainMastery);
                setLastPracticedByDomain(stats.lastPracticedByDomain || {});

                // Load or migrate ability estimate
                if (stats.abilityEstimate) {
                    setAbilityEstimate(stats.abilityEstimate);
                } else if (stats.totalQuestions > 0) {
                    const oldAbility = (stats.correctAnswers / stats.totalQuestions) * 2 - 1;
                    const migrated = migrateFromDeltaRule(oldAbility);
                    setAbilityEstimate(migrated);
                    updateAbilityEstimate(user.uid, migrated);
                }

                // Initialize first card
                setCurrentCard(selectNextCard());
                setCardStartTime(performance.now());
            } catch (error) {
                console.error('Error loading user data:', error);
                setError('Failed to load user data. Please try again later.');
            } finally {
                setIsLoading(false);
            }
        };

        loadUserData();
    }, [user]);

    if (isLoading) {
        return (
            <div className="min-h-screen bg-[#070B14] text-gray-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto"></div>
                    <p className="mt-4">Loading your progress...</p>
                </div>
            </div>
        );
    }

    // Main return statement
    return (
        <div className="min-h-screen bg-[#070B14] text-gray-100">
            {/* Ambient glow */}
            <div className="pointer-events-none fixed inset-0 overflow-hidden">
                <div className="absolute -top-40 left-1/3 h-[520px] w-[820px] rounded-full bg-cyan-500/10 blur-3xl" />
                <div className="absolute bottom-[-220px] right-[-220px] h-[560px] w-[560px] rounded-full bg-blue-500/10 blur-3xl" />
            </div>

            {/* Hamburger — only visible when sidebar is closed on mobile */}
            {!isSidebarOpen && (
                <button
                    onClick={() => setSidebarOpen(true)}
                    className="fixed top-4 left-4 z-50 md:hidden rounded-xl bg-white/10 p-2.5 text-white ring-1 ring-white/10 backdrop-blur"
                    aria-label="Open menu"
                >
                    <Menu size={24} />
                </button>
            )}

            <nav
                className={`fixed top-0 left-0 z-40 h-screen w-full md:w-64 overflow-y-auto md:overflow-hidden md:flex md:flex-col border-r border-white/[0.08] bg-gradient-to-b from-white/[0.06] to-white/[0.02] backdrop-blur-2xl ring-1 ring-inset ring-white/[0.08] shadow-[inset_0_1px_0_0_rgba(255,255,255,0.08),0_0_40px_-10px_rgba(6,182,212,0.08)] transition-transform duration-200 ease-in-out md:translate-x-0 ${isSidebarOpen ? "translate-x-0" : "-translate-x-full"}`}
            >
                {/* Sidebar content */}
                <div className="p-6 border-b border-white/10 flex items-start justify-between">
                    <div>
                        <h1 className="text-xl font-bold text-blue-400 mb-2">
                            Security+ Crammer
                        </h1>
                        <div className="text-sm text-white/70">
                            Flashcards
                        </div>
                    </div>
                    <button
                        onClick={() => setSidebarOpen(false)}
                        className="md:hidden rounded-xl bg-white/10 p-2 text-white ring-1 ring-white/10 flex-shrink-0"
                        aria-label="Close menu"
                    >
                        <X size={20} />
                    </button>
                </div>

                <div className="md:flex-grow md:overflow-y-auto p-4">
                    <ul className="space-y-3 mb-6">
                        <li>
                            <Link to="/reports" className="flex items-center px-3 py-2 rounded-lg text-gray-300 hover:bg-white/10">
                                <ChartBar size={18} className="mr-3" />
                                <span>Reports</span>
                            </Link>
                        </li>
                        <li>
                            <Link to="/practice" className="flex items-center px-3 py-2 rounded-lg text-gray-300 hover:bg-white/10">
                                <Target size={18} className="mr-3" />
                                <span>Practice</span>
                            </Link>
                        </li>
                        <li>
                            <Link to="/flashcards" className="flex items-center px-3 py-2 rounded-lg bg-gradient-to-r from-cyan-600 to-blue-600 text-white">
                                <Brain size={18} className="mr-3" />
                                <span>Flashcards</span>
                            </Link>
                        </li>
                    </ul>

                    <div className="rounded-xl bg-white/5 p-4 ring-1 ring-white/10">
                        <h3 className="text-sm font-semibold text-white/55 mb-3">Domain Mastery</h3>
                        {Object.entries(domainMastery).map(([domain, mastery]) => (
                            <div key={domain} className="mb-3">
                                <div className="flex justify-between mb-1">
                                    <span className="text-xs text-white">{domain}</span>
                                    <span className="text-xs text-white">
                                        {Math.min(100, Math.max(0, mastery * 100)).toFixed(1)}%
                                    </span>
                                </div>
                                <div className="w-full bg-white/10 rounded-full h-1">
                                    <div
                                        className="bg-blue-400 h-1 rounded-full transition-all duration-300"
                                        style={{ width: `${Math.min(100, Math.max(0, mastery * 100))}%` }}
                                    ></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="p-4 px-6 md:px-4 pb-10 md:pb-4 md:border-t border-white/10 space-y-3">
                    {!isPaidUser && (
                        <Link to="/pricing" className="block">
                            <div className="w-full rounded-xl bg-gradient-to-r from-cyan-600/10 to-blue-600/10 ring-1 ring-cyan-500/30 hover:ring-cyan-500/50 px-3 py-2 transition-colors group flex items-center justify-center">
                                <Crown size={14} className="text-cyan-400 mr-2" />
                                <span className="text-xs font-semibold text-cyan-400">Upgrade</span>
                                <span className="text-xs text-white/45 ml-1.5">· Unlock all modes</span>
                            </div>
                        </Link>
                    )}
                    <Link to="/" className="block">
                        <button className="w-full rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 px-3 py-3 text-white flex items-center justify-center">
                            <ArrowLeft size={18} className="mr-3" />
                            <span className="text-sm">Back to Overview</span>
                        </button>
                    </Link>
                    <button onClick={() => signOut(auth)} className="w-full rounded-xl bg-white/5 hover:bg-white/10 ring-1 ring-red-500/30 px-3 py-3 text-red-400 flex items-center justify-center text-sm">
                        <LogOut size={18} className="mr-3" />
                        Sign Out
                    </button>
                </div>
            </nav>

            <main className="relative md:ml-64 p-4 md:p-8">
                <div className="max-w-4xl mx-auto">
                    <div className="mb-6">
                        <div className="flex justify-between items-center">
                            <h2 className="text-xl font-bold text-blue-400">Flashcards</h2>
                            <div className="flex items-center space-x-4">
                                <div className="text-sm text-white/55">
                                    Reviewed: {sessionStats.reviewed} | Correct: {sessionStats.correct} | Streak: {sessionStats.streak}
                                </div>
                            </div>
                        </div>
                    </div>

                    {currentCard && (
                        <div className="mb-8">
                            <div
                                onClick={handleCardFlip}
                                className="rounded-2xl bg-white/5 p-8 ring-1 ring-white/10 cursor-pointer min-h-[300px] flex items-center justify-center text-center transition-all duration-300 hover:bg-white/10"
                            >
                                <div className="max-w-2xl">
                                    <h3 className="text-xl font-semibold mb-4 text-blue-400">
                                        {isFlipped ? "Definition" : "Term"}
                                    </h3>
                                    <p className="text-lg">
                                        {isFlipped ? currentCard.definition : currentCard.term}
                                    </p>
                                </div>
                            </div>

                            {isFlipped && (
                                <div className="mt-6 flex justify-center space-x-4">
                                    <button
                                        onClick={() => handleResponse(false)}
                                        className="px-6 py-3 bg-red-600 hover:bg-red-700 rounded-xl flex items-center"
                                    >
                                        <ThumbsDown size={18} className="mr-2" />
                                        Incorrect
                                    </button>
                                    <button
                                        onClick={() => handleResponse(true)}
                                        className="px-6 py-3 bg-green-600 hover:bg-green-700 rounded-xl flex items-center"
                                    >
                                        <ThumbsUp size={18} className="mr-2" />
                                        Correct
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </main>
        </div>
    );
};

export default Flashcards;